// frontend/src/pages/Returns.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../api';
import { Link } from 'react-router-dom';
import { authorizedUrl } from '../utils/authLink';

/* ----------------------------- feature flags ------------------------------ */
const ALLOW_MANUAL_ADD = false; // ← keep false to hide "+ Add Item"

/* ----------------------------- utils ----------------------------- */
const n = (v) => (v === '' || v == null ? 0 : Number(v));
const clamp = (x, lo, hi) => Math.max(lo, Math.min(hi, Number(x || 0)));
const money = (x) => `₹${Number(x || 0).toFixed(2)}`;
const debounce = (fn, ms) => { let t; return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); }; };
const round2 = (x) => Number((Math.round(Number(x || 0) * 100) / 100).toFixed(2));
const isBlankEdit = (v) => v === '' || v === '.'; // <-- allow temporary blank while typing

const blankRow = () => ({
  selected: false,
  product_id: '',
  name: '',
  qty: 0,
  purchasedQty: 0,
  returnedQty: 0,
  maxQty: 0,
  price: 0,
  gst_rate: 0,
  discount_pct: 0, // keep per-line discount %
});

const inputFull = { width: '100%', boxSizing: 'border-box' };

/* ----------------------------- theme tokens ----------------------------- */
const CSS = {
  border:  '1px solid var(--hairline, #e5e7eb)',
  cardBg:  'var(--card, #ffffff)',
  cardFg:  'var(--fg, #0b1220)',
  chip:    'var(--code-bg, #f1f5f9)',
  muted:   'var(--muted, #475569)',
  accent:  'var(--accent, #7c3aed)',
  accentFg:'var(--accent-contrast, #ffffff)',
};

/* ----------------------------- page ------------------------------ */
export default function Returns() {
  const [form, setForm] = useState({
    original_invoice_no: '',
    customer_name: '',
    customer_phone: '',
    reason: '',
    // IMPORTANT: start with NO rows; must load from invoice
    items: [],
  });

  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Store the last created return to render the credit note card
  const [lastReturn, setLastReturn] = useState(null);

  // copy-to-clipboard UI state
  const [copied, setCopied] = useState(false);
  const copyReturnNo = async () => {
    const text = String(lastReturn?.return_no || '').trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 1500); }
      finally { document.body.removeChild(ta); }
    }
  };
  const tinyBtn = {
    marginLeft: 8,
    fontSize: 12,
    padding: '2px 8px',
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    background: '#f8fafc',
    cursor: 'pointer',
  };

  // suggestions
  const [suggestRow, setSuggestRow] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const boxRef = useRef(null);

  /* ------------------------ derived totals ------------------------ */
  const totals = useMemo(() => {
    let subtotal = 0, discount = 0, gst = 0;

    for (const it of form.items || []) {
      const qty = n(it.qty);
      const price = n(it.price);
      const discPct = clamp(n(it.discount_pct), 0, 100);
      const gstRate = clamp(n(it.gst_rate), 0, 100);

      const lineGross = qty * price;                  // base before discount
      const lineDisc  = round2(lineGross * (discPct / 100));
      // Do NOT round the net before computing GST — compute GST from the more precise net
      const lineNetExact = (lineGross - lineDisc);
      const lineGst   = round2(lineNetExact * (gstRate / 100));

      subtotal += lineGross;
      discount += lineDisc;
      gst      += lineGst;
    }

    subtotal = round2(subtotal);
    discount = round2(discount);
    gst      = round2(gst);

    return { subtotal, discount, tax: gst, total: round2(subtotal - discount + gst) };
  }, [form.items]);

  /* ------------------------ helpers/mutators ---------------------- */
  const setItem = (idx, patch) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    }));
  };

  // Keep add/remove for internal use, but hide UI trigger when ALLOW_MANUAL_ADD=false
  const addItem = () => setForm((p) => ({ ...p, items: [...p.items, blankRow()] }));
  const removeItem = (idx) =>
    setForm((p) => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));

  const selectAll = () =>
    setForm((p) => ({ ...p, items: p.items.map((it) => ({ ...it, selected: true })) }));
  const clearSelected = () =>
    setForm((p) => ({ ...p, items: p.items.map((it) => ({ ...it, selected: false })) }));
  const fillMaxForSelected = () =>
    setForm((p) => ({
      ...p,
      items: p.items.map((it) =>
        it.selected ? { ...it, qty: clamp(it.maxQty, 0, it.maxQty || 0) } : it
      ),
    }));

  /* ------------------------ invoice loader ------------------------ */
  const loadFromInvoice = async () => {
    setMessage('');
    const inv = form.original_invoice_no.trim();
    if (!inv) { setMessage('Enter an Invoice No first'); return; }
    try {
      // This endpoint should return: { invoice, items: [...] }
      const r = await api.get(`/invoices/by-no/${encodeURIComponent(inv)}/items`);
      const { invoice, items } = r.data || {};

      const mapped =
        (items || []).map((it) => {
          const price   = n(it.price);
          const net     = (it.net_price != null) ? n(it.net_price) : null;
          const discPI  = (it.discount_per_item != null) ? n(it.discount_per_item) : null;
          const lineDiscTotal = (it.discount != null) ? n(it.discount) : null; // total discount for full invoice line
          const invQty  = n(it.qty ?? it.purchasedQty ?? it.purchased_qty);

          // Prefer explicit discount_pct; else derive from net or per-item/total discount
          let discountPct = clamp(n(it.discount_pct), 0, 100);
          if (!discountPct) {
            if (price > 0 && net != null) {
              discountPct = clamp(round2((1 - net / price) * 100), 0, 100);
            } else if (price > 0 && discPI != null) {
              discountPct = clamp(round2((discPI / price) * 100), 0, 100);
            } else if (price > 0 && lineDiscTotal != null && invQty > 0) {
              // derive per-item discount from total discount / qty
              const perItem = lineDiscTotal / invQty;
              discountPct = clamp(round2((perItem / price) * 100), 0, 100);
            }
          }

          const purchasedQty = n(it.purchasedQty ?? it.purchased_qty ?? it.qty);
          const returnedQty  = n(it.returnedQty ?? it.returned_qty);
          const maxQty       = n(it.maxQty ?? (purchasedQty - returnedQty));

          return {
            selected: false,
            product_id: it.product_id ?? '',
            name: it.name ?? '',
            qty: 0, // user chooses how many to return (partial return supported)
            purchasedQty,
            returnedQty,
            maxQty,
            price: price,
            gst_rate: n(it.gst_rate),
            discount_pct: discountPct, // carry original invoice line discount
          };
        }) || [];

      setForm((prev) => ({
        ...prev,
        customer_name: invoice?.customer_name || invoice?.customer?.name || '',
        customer_phone: invoice?.customer_phone || invoice?.customer?.phone || '',
        items: mapped, // ← replace entirely, don't leave a blank row
      }));
      setMessage(`Loaded ${mapped.length} item${mapped.length === 1 ? '' : 's'}.`);
    } catch {
      setMessage('Error: failed to fetch invoice items');
    }
  };

  /* -------------------- product name suggestions ------------------ */
  const runSuggest = debounce(async (text, idx) => {
    if ((text || '').trim().length < 2) {
      setSuggestions([]); setSuggestRow(null); return;
    }
    const normalize = (res) => {
      const d = res?.data ?? res;
      if (Array.isArray(d)) return d;
      if (Array.isArray(d?.items)) return d.items;
      return [];
    };
    try {
      const r1 = await api.get('/products/search', { params: { q: text, _ts: Date.now() }, validateStatus: () => true });
      let items = r1.status === 200 ? normalize(r1) : [];
      if (!items.length) {
        const r2 = await api.get('/products', { params: { q: text, _ts: Date.now() }, validateStatus: () => true });
        items = r2.status === 200 ? normalize(r2) : [];
      }
      setSuggestions(items);
      setSuggestRow(items.length ? idx : null);
    } catch {
      setSuggestions([]); setSuggestRow(null);
    }
  }, 200);

  const pickSuggestion = (idx, s) => {
    setItem(idx, {
      product_id: s.id ?? s.product_id ?? '',
      name: s.name ?? s.title ?? '',
      price: n(s.price ?? s.mrp ?? s.sale_price ?? 0),
      gst_rate: clamp(n(s.gst_rate ?? s.gst_percent ?? s.taxRate ?? s.tax ?? 0), 0, 100),
      // When manually picking a product (not from invoice), default discount to 0
      discount_pct: 0,
    });
    setSuggestions([]);
    setSuggestRow(null);
  };

  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setSuggestRow(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  /* ----------------------------- submit --------------------------- */
  const submit = async () => {
    setSubmitting(true); setMessage('');
    try {
      const cleanItems = (form.items || [])
        .filter((it) => it.selected)
        .map((it) => ({
          product_id: it.product_id ? Number(it.product_id) : null,
          name: it.name || '',
          qty: clamp(n(it.qty), 0, n(it.maxQty || Infinity)),
          price: n(it.price),
          gst_rate: clamp(n(it.gst_rate), 0, 100),
          discount_pct: clamp(n(it.discount_pct), 0, 100), // send to backend
          maxQty: n(it.maxQty || 0),
        }))
        .filter((it) => it.qty > 0);

      if (!cleanItems.length) {
        setMessage('Please select at least one line and set qty > 0.');
        setSubmitting(false);
        return;
      }

      const payload = {
        original_invoice_no: form.original_invoice_no || null,
        customer_name: form.customer_name || null,
        customer_phone: form.customer_phone || null,
        reason: form.reason || '',
        items: cleanItems,
      };

      const resp = await api.post('/returns', payload);
      const data = resp.data;

      // Save for "Last Return" card
      setLastReturn(data);

      const tax = round2(n(data.totals.cgst) + n(data.totals.sgst) + n(data.totals.igst));
      setMessage(
        `Return ${data.return_no} — Subtotal ${money(data.totals.subtotal)}, ` +
        `Discount ${money(data.totals.discount)}, ` +
        `Tax ${money(tax)} ` +
        `(CGST ${money(data.totals.cgst)}, SGST ${money(data.totals.sgst)}, IGST ${money(data.totals.igst)}), ` +
        `Total credit ${money(Math.abs(data.totals.total))}`
      );

      // Reset lines for a fresh form (keep no blank row)
      setForm((prev) => ({ ...prev, items: [] }));
    } catch (e) {
      setMessage('Error: ' + (e?.response?.data?.error || e.message || 'Failed'));
    } finally {
      setSubmitting(false);
    }
  };

  // Build Credit Note PDF URL (by **invoice** number not return number)
  const invNoForPdf = String(
    lastReturn?.original_invoice_no ||
    lastReturn?.invoice_no ||
    form.original_invoice_no ||
    ''
  ).trim();

  const creditPdfHref = lastReturn && invNoForPdf
    ? authorizedUrl(`/api/credit-notes/by-invoice/${encodeURIComponent(invNoForPdf)}/pdf`)
    : '#';

  /* ------------------------------ UI -------------------------------- */
  return (
    <div className="container" ref={boxRef}>
      {/* Header: Invoice loader */}
      <div className="card" style={{ marginTop: 8, padding: 12, overflow: 'hidden', background: CSS.cardBg, color: CSS.cardFg, border: CSS.border }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <input
            className="input"
            placeholder="Invoice No"
            value={form.original_invoice_no}
            onChange={(e) => setForm({ ...form, original_invoice_no: e.target.value })}
            style={{ ...inputFull, flex: '1 1 260px', minWidth: 240 }}
          />
          <button className="btn" onClick={loadFromInvoice}>Load Items</button>
          <Link className="btn secondary" to="/invoice">Go to Invoice</Link>
        </div>
      </div>

      {/* Customer info */}
      <div className="card" style={{ marginTop: 12, padding: 12, overflow: 'hidden', background: CSS.cardBg, color: CSS.cardFg, border: CSS.border }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr) minmax(0,1fr)',
            gap: 12,
          }}
        >
          <input
            className="input"
            placeholder="Customer Name (optional)"
            value={form.customer_name}
            onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
            style={inputFull}
          />
          <input
            className="input"
            placeholder="Customer Phone (optional)"
            type="tel"
            value={form.customer_phone}
            onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
            style={inputFull}
          />
          <input
            className="input"
            placeholder="Reason (optional)"
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            style={inputFull}
          />
        </div>
      </div>

      {/* Items */}
      <div className="card returns-items" style={{ marginTop: 12, padding: 12, overflow: 'visible', background: CSS.cardBg, color: CSS.cardFg, border: CSS.border }}>
        <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h3 style={{ margin: 0 }}>Items</h3>
          <div className="flex returns-controls" style={{ gap: 8 }}>
            <button className="btn secondary" onClick={selectAll}>Select all</button>
            <button className="btn secondary" onClick={fillMaxForSelected}>Return max</button>
            <button className="btn secondary" onClick={clearSelected}>Clear selected</button>
            {/* "+ Add Item" is intentionally hidden */}
            {ALLOW_MANUAL_ADD && (
              <button className="btn" onClick={addItem}>+ Add Item</button>
            )}
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="table" style={{ overflow: 'visible' }}>
            <thead>
              <tr>
                <th style={{ width: 64 }}>Pick</th>
                <th>Name (type to search)</th>
                <th style={{ width: 170 }}>Max</th>
                <th style={{ width: 120 }}>Qty</th>
                <th style={{ width: 140 }}>Price</th>
                <th style={{ width: 120 }}>Disc %</th>
                <th style={{ width: 120 }}>Tax %</th>
                <th style={{ width: 90 }}></th>
              </tr>
            </thead>
            <tbody>
              {(form.items.length ? form.items : []).map((it, idx) => (
                <tr key={idx} style={{ position: 'relative' }}>
                  <td>
                    <input
                      type="checkbox"
                      checked={!!it.selected}
                      onChange={(e) => setItem(idx, { selected: e.target.checked })}
                    />
                  </td>

                  {/* Name + suggestions */}
                  <td style={{ position: 'relative', minWidth: 220 }}>
                    <input
                      className="input"
                      value={it.name}
                      onChange={(e) => { setItem(idx, { name: e.target.value }); runSuggest(e.target.value, idx); }}
                      placeholder="Type name"
                      style={{ ...inputFull, minWidth: 200 }}
                      readOnly={!ALLOW_MANUAL_ADD} // lock typing when manual add is disabled
                    />
                    {ALLOW_MANUAL_ADD && suggestRow === idx && suggestions.length > 0 && (
                      <div
                        className="card"
                        style={{
                          marginTop: 6,
                          border: CSS.border,
                          background: CSS.cardBg,
                          color: CSS.cardFg,
                          borderRadius: 6,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
                          maxHeight: 220,
                          overflowY: 'auto',
                          zIndex: 40,
                          position: 'relative',
                        }}
                      >
                        {suggestions.map((s, i) => (
                          <div
                            key={`${s.id || s.product_id || i}`}
                            onMouseDown={() => pickSuggestion(idx, s)}
                            style={{
                              padding: '8px 10px',
                              cursor: 'pointer',
                              display: 'grid',
                              gridTemplateColumns: '1fr auto',
                              gap: 8,
                            }}
                          >
                            <div className="mono" style={{ fontWeight: 600 }}>{s.name || s.title || 'Unnamed'}</div>
                            <div className="mono">{money(n(s.price ?? s.mrp ?? s.sale_price ?? 0))}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </td>

                  {/* Max with badge */}
                  <td style={{ minWidth: 170, overflow: 'visible' }}>
                    <div className="flex" style={{ gap: 6, alignItems: 'center', flexWrap: 'nowrap' }}>
                      <input className="input mono" value={it.maxQty} readOnly style={{ width: 60 }} />
                      <button
                        className="btn btn-sm secondary"
                        type="button"
                        title="Set Qty to Max"
                        onClick={() => setItem(idx, { qty: n(it.maxQty) })}
                      >
                        ↦
                      </button>
                      <span
                        className="mono"
                        title={`Remaining: ${n(it.maxQty)} (Purchased ${n(it.purchasedQty)} − Returned ${n(it.returnedQty)})`}
                        style={{
                          fontSize: 12,
                          background: CSS.chip,
                          border: CSS.border,
                          color: CSS.cardFg,
                          padding: '2px 6px',
                          borderRadius: 6,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Rem: {n(it.maxQty)}
                      </span>
                    </div>
                  </td>

                  {/* Editable numbers (still editable for invoice-loaded lines) */}
                  <td>
                    <input
                      className="input mono"
                      type="number"
                      step="1"
                      min="0"
                      value={it.qty}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (isBlankEdit(v)) {
                          setItem(idx, { qty: '' });
                        } else {
                          const num = Math.trunc(Number(v));
                          setItem(idx, { qty: clamp(num, 0, n(it.maxQty || Infinity)) });
                        }
                      }}
                      style={{ width: 100 }}
                    />
                  </td>

                  <td>
                    <input
                      className="input mono"
                      type="number"
                      step="1"
                      min="0"
                      value={it.price}
                      onChange={(e) => {
                        const v = e.target.value;
                        setItem(idx, { price: isBlankEdit(v) ? '' : clamp(v, 0, 99999999) });
                      }}
                      style={{ width: 120 }}
                      readOnly={!ALLOW_MANUAL_ADD}
                    />
                  </td>

                  <td>
                    <input
                      className="input mono"
                      type="number"
                      step="1"
                      min="0"
                      max="100"
                      value={it.discount_pct}
                      onChange={(e) => {
                        const v = e.target.value;
                        setItem(idx, { discount_pct: isBlankEdit(v) ? '' : clamp(v, 0, 100) });
                      }}
                      style={{ width: 120 }}
                      readOnly={!ALLOW_MANUAL_ADD}
                    />
                  </td>

                  <td>
                    <input
                      className="input mono"
                      type="number"
                      step="1"
                      min="0"
                      max="100"
                      value={it.gst_rate}
                      onChange={(e) => {
                        const v = e.target.value;
                        setItem(idx, { gst_rate: isBlankEdit(v) ? '' : clamp(v, 0, 100) });
                      }}
                      style={{ width: 100 }}
                      readOnly={!ALLOW_MANUAL_ADD}
                    />
                  </td>

                  <td>
                    <button className="btn danger" onClick={() => removeItem(idx)}>Remove</button>
                  </td>
                </tr>
              ))}
              {!form.items.length && (
                <tr>
                  <td colSpan={8} className="mono" style={{ opacity: 0.7 }}>
                    Load an invoice to see its items.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Totals chips */}
        <div className="flex returns-totals" style={{ gap: 12, justifyContent: 'flex-end', marginTop: 10 }}>
          <span className="mono" style={{ background: CSS.chip, padding: '6px 10px', borderRadius: 6, border: CSS.border }}>
            Subtotal: {money(totals.subtotal)}
          </span>
          <span className="mono" style={{ background: CSS.chip, padding: '6px 10px', borderRadius: 6, border: CSS.border }}>
            Discount: {money(totals.discount)}
          </span>
          <span className="mono" style={{ background: CSS.chip, padding: '6px 10px', borderRadius: 6, border: CSS.border }}>
            GST: {money(totals.tax)}
          </span>
          <span className="mono" style={{ background: CSS.chip, padding: '6px 10px', border: CSS.border, borderRadius: 6, fontWeight: 700 }}>
            Total: {money(totals.total)}
          </span>
        </div>
      </div>

      {/* Footer actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
        <button className="btn primary" disabled={submitting} onClick={submit}>
          {submitting ? 'Submitting…' : 'Create Return'}
        </button>
        {message && <div className="mono" style={{ color: CSS.muted }}>{message}</div>}
      </div>

      {/* Last Return / Credit Note */}
      <div className="card" style={{ marginTop: 12, padding: 12, background: CSS.cardBg, color: CSS.cardFg, border: CSS.border }}>
        <div className="mono" style={{ marginBottom: 6 }}>Last Return</div>
        {lastReturn ? (
          <div>
            <div className="mono" style={{ display:'flex', alignItems:'center', flexWrap:'wrap', gap:8 }}>
              <span
                title="Click to copy return number"
                onClick={copyReturnNo}
                style={{ cursor:'pointer', userSelect:'none' }}
              >
                {lastReturn.return_no}
              </span>
              <span>· Credit: {money(Math.abs(lastReturn?.totals?.total ?? 0))}</span>

              {/* Show the invoice the credit note will be generated against */}
              {invNoForPdf && (
                <span className="mono" style={{ fontSize: 12, opacity: 0.8 }}>
                  · Invoice: {invNoForPdf}
                </span>
              )}

              <button
                type="button"
                aria-label="Copy return number"
                title="Copy return number"
                onClick={copyReturnNo}
                style={tinyBtn}
              >
                📋 Copy
              </button>
              {copied && (
                <span className="mono" style={{ fontSize: 12, color: '#065f46' }}>
                  ✓ Copied!
                </span>
              )}
            </div>
            <div style={{ marginTop: 8 }}>
              <a className="btn" href={creditPdfHref} target="_blank" rel="noreferrer">
                Open Credit Note PDF
              </a>
            </div>
          </div>
        ) : (
          <div className="mono" style={{ opacity: 0.8 }}>No return yet.</div>
        )}
      </div>
    </div>
  );
}
